import { AssetFeatureCollectionResponse, AssetFeatureResponse } from "./types/stores";
import Component from "./components/component";
import MapComponent, { MapComponentEvents } from "./components/map/map";
import SearchComponent, { SearchComponentEvents, SearchLocation } from "./components/search/search";
import StoresListComponent, { StoresListComponentEvents } from "./components/storeslist/stores_list";
import "./styles/main.scss";
import { getLocale, getLocaleLang, setUserLocale, SYSTEM_LANG } from './helpers/locale';
import { debounce, getArrowSymbol, parseURLParameters } from "./utils/utils";
import { Configuration, getConfig, setConfig } from "./configuration/config";
import { preloadLink } from "./utils/preload";
import { WoosmapApiClient } from "./services/woosmap_api";
import { GeoLocationResponse } from "./types/geolocation";
import GeoJSONFeature = woosmap.map.GeoJSONFeature;
import bludeDot from './assets/geolocated_marker.png';



export interface IStoreLocator {
    initialSearch?: string;
    padding: woosmap.map.Padding;
    locale?: string;
    config?: Configuration;
}

export enum StoreLocatorEvents {
    PADDING_CHANGED = "padding_changed",
    CTA_TARIFFS = "cta_tariffs"
}

export enum StoreLocatorViews {
    LIST_VIEW = "list_view",
}

export default class StoreLocator extends Component<IStoreLocator> {
    public searchComponent!: SearchComponent;
    public mapComponent!: MapComponent;
    public storesListComponent!: StoresListComponent;
    private $sidebarContentContainer!: HTMLElement;
    private showPanel!: boolean;
    private api!: WoosmapApiClient;
    private blueDotMarker!: woosmap.map.Marker;
    private storeHighlight!: boolean;
    private shouldHideSearchContainer!: boolean;
    private isForSuccessPage!: boolean;
    private woosGeoLocation!: GeoLocationResponse;
    private allStores!: woosmap.map.stores.StoreResponse[];
    private nearestStoreToUser!: AssetFeatureResponse;

    init(): void {
        this.showPanel = false;

    }

    render(): void {
        setUserLocale(this.state.locale || SYSTEM_LANG);
        setConfig(this.state.config);
        this.preloadLinks();
        const urlParams = parseURLParameters(window.location.search);
        let highlightStore: string | undefined = undefined;
        if (urlParams && urlParams['store']) {
            highlightStore = urlParams['store'] as string;
            this.storeHighlight = true;
            this.isForSuccessPage = urlParams['success'] === 'true';
        }
        this.shouldHideSearchContainer = this.storeHighlight || this.isForSuccessPage;
        this.$target.innerHTML = "";
        this.$target.insertAdjacentHTML("afterbegin", this.getHTMLSkeleton());
        this.$sidebarContentContainer = document.getElementById(
            getConfig().selectors.sidebarContentContainerID
        ) as HTMLElement;
        this.styleOnScroll();
        this.mapComponent = new MapComponent({
            $target: document.getElementById(getConfig().selectors.mapContainerID) as HTMLElement,
            initialState: {
                woosmapPublicKey: getConfig().map.woosmapPublicKey,
                mapOptions: getConfig().map.mapOptions,
                padding: this.state.padding,
                filter: getConfig().map.baseFilter,
                isHighlightMode: this.storeHighlight
            },
        });
        let searchLocation: SearchLocation | undefined = undefined;

        if (urlParams && urlParams['search'] && urlParams['lat'] && urlParams['lng']) {
            searchLocation = {
                name: urlParams['search'] as string,
                location: {
                    lat: urlParams['lat'] as number,
                    lng: urlParams['lng'] as number
                }
            };
        }
        if (urlParams && urlParams['store']) {
            highlightStore = urlParams['store'] as string;
        }

        this.searchComponent = new SearchComponent({
            $target: document.getElementById(
                getConfig().selectors.searchWrapperID
            ) as HTMLElement,
            initialState: {
                inputID: getConfig().selectors.searchInputID,
                woosmapPublicKey: getConfig().map.woosmapPublicKey,
                searchOptions: getConfig().search.localitiesConf,
                featuresBtn: ["search", "clear", "geolocate"],
                selectedLocality: searchLocation
            },
        });
        this.storesListComponent = new StoresListComponent({
            $target: document.getElementById(
                getConfig().selectors.listStoresContainerID
            ) as HTMLElement,
            initialState: {
                query: getConfig().map.baseFilter
            },
        });
        this.showSideBarPanel();
        this.searchComponent.on(SearchComponentEvents.SELECTED_LOCALITY, (locality: SearchLocation) => {
            this.storesListComponent.setState({
                nearbyLocation: locality.location,
                query: getConfig().map.baseFilter,
                selectedStore: undefined
            }, true, () =>
                this.storesListComponent.emit(StoresListComponentEvents.LOCALITY_CHANGED)
            );
            setTimeout(() => {
                this.showBlueDot(locality.location);
            }, 100);

        }
        );
        this.searchComponent.on(SearchComponentEvents.SEARCH_CLEAR, () => {
            this.showPanel = false;
            this.storesListComponent.setState({ nearbyLocation: undefined, stores: undefined, selectedStore: undefined });
            this.mapComponent.setState({ selectedStore: undefined, stores: undefined, nearbyLocation: undefined }, true);
            if (this.allStores && this.allStores.length > 0) {
                this.mapComponent.loadInitialMarkers(this.allStores, false);
            }
            this.setListView();
        }
        );
        this.searchComponent.on(SearchComponentEvents.WOOS_GEOLOCATION_SET, (position: GeoLocationResponse) => {
            this.woosGeoLocation = position;
        }
        );
        this.storesListComponent.on(StoresListComponentEvents.STORES_CHANGED, (stores: GeoJSONFeature[], nearbyLocation: woosmap.map.LatLngLiteral) => {
            this.showPanel = true;
            this.mapComponent.setState({ selectedStore: undefined }, true);
            this.storesListComponent.setState({ selectedStore: undefined }, true);
            this.mapComponent.setState({ stores: stores, nearbyLocation }, true, () =>
                this.mapComponent.emit(MapComponentEvents.STORES_CHANGED)
            );
            this.setListView();
        });
        this.storesListComponent.on(StoresListComponentEvents.STORE_SELECTED, (selectedStore: AssetFeatureResponse) => {
            this.getPositionOfSelectedStore(selectedStore);
            this.mapComponent.setState({ selectedStore: selectedStore }, true, () =>
                this.mapComponent.selectStore()
            );
            if (this.storeHighlight) {
                this.adjustPanelHeight();
                this.$sidebarContentContainer.querySelector("li")?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
        );
        this.storesListComponent.on(StoresListComponentEvents.STORE_MOUSEENTER, debounce((selectedStore: AssetFeatureResponse) => {
            this.mapComponent.selectStoreOnDataOverlay(selectedStore);
        }, 50));
        this.storesListComponent.on(StoresListComponentEvents.STORE_MOUSELEAVE, debounce(() => {
            this.mapComponent.unselectStoreOnDataOverlay();
        }, 50));
        this.storesListComponent.on(StoresListComponentEvents.CTA_TARIFFS_CLICKED, (selectedStore: AssetFeatureResponse) => {
            this.emit(StoreLocatorEvents.CTA_TARIFFS, selectedStore);
        }
        );
        this.mapComponent.on(MapComponentEvents.STORE_UNSELECTED, (stores) => {
            const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
            if (width < getConfig().map.mobileBreakPoint) {
                this.storesListComponent.setState({ selectedStore: undefined });
            }
            else {
                this.storesListComponent.setState({ selectedStore: undefined, stores: stores?.length ? stores : undefined });
                this.setListView(false);
            }
        });
        this.mapComponent.on(MapComponentEvents.STORE_SELECTED, (selectedStore: AssetFeatureResponse, stores: GeoJSONFeature[]) => {
            if (selectedStore) {
                this.showPanel = true;
                if (stores && stores.length > 0) {
                    this.storesListComponent.setState({ selectedStore: selectedStore });
                } else {
                    this.storesListComponent.setState({ selectedStore: selectedStore, stores: [selectedStore] });
                }

                this.setListView(false);
                this.scrollToSelectedStore(selectedStore);
            } else {
                this.setListView();
            }
        });
        this.on(StoreLocatorEvents.PADDING_CHANGED, () => {
            this.mapComponent.setState({ padding: this.state.padding }, true);
        });
        this.mapComponent.on(MapComponentEvents.MAP_READY, () => {
            this.managePadding();
            this.initView();
        });
        this.mapComponent.on(MapComponentEvents.MAP_IDLE, async () => {
            if (!searchLocation && !highlightStore) {
                // Sequenced to avoid a camera race: getAllStores must complete and
                // fitBounds before getUserPosition flies to the user, otherwise the
                // two camera ops interleave (stuck bounds, missing markers).
                // Tradeoff: a brief "fit-to-stores → fly-to-user" flash is visible.
                try {
                    const stores = await this.getAllStores();
                    this.allStores = stores;
                    this.mapComponent.loadInitialMarkers(stores);
                } catch (err) {
                    console.error("Initial store load failed:", err);
                }
                this.getUserPosition();
            }
            if (highlightStore) {
                this.locateStore(highlightStore);
            }
            this.manageMarkerFocus();
        })
        window.addEventListener('resize', debounce(() => {
            this.managePadding();
            // this.updateMapControlPosition();
            if (this.storeHighlight) {
                this.adjustPanelHeight();
            }
        }, 100))
        this.mapComponent.on(MapComponentEvents.MAP_BOUNDS_CHANGED, (bounds: woosmap.map.LatLngBounds) => {
            if (this.nearestStoreToUser && this.allStores) {
                const storesInBounds = this.filterStoresByBounds(this.allStores, bounds);
                if (storesInBounds && storesInBounds.length === 1) {
                    this.mapComponent.showSingleStoreMarker(this.nearestStoreToUser);
                }
                else if (storesInBounds && storesInBounds.length > 1) {
                    this.mapComponent.hideSingleStoreMarker();
                }
            }
        })
    }

    setListView(focusSearch = true): void {
        this.$target.className = StoreLocatorViews.LIST_VIEW;
        this.$sidebarContentContainer.scrollTo(0, 0);
        // this.$sidebarContentContainer.querySelector("li")?.focus();
        if (focusSearch) {
            const searchInput = document.getElementById(getConfig().selectors.searchInputID) as HTMLInputElement;
            searchInput.focus();
        }
        this.showSideBarPanel();
    }

    styleOnScroll(): void {
        this.$sidebarContentContainer?.addEventListener("scroll", () => {
            const scroll = this.$sidebarContentContainer.scrollTop;
            if (scroll > 0) {
                this.$sidebarContentContainer.classList.add("active");
            } else {
                this.$sidebarContentContainer.classList.remove("active");
            }
        }, { passive: true });
    }

    initView(): void {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        if (width > getConfig().map.mobileBreakPoint) {
            this.setFullView();
            this.buildMapPanControl();
        } else {
            this.setMobileView();
        }
    }

    managePadding(): void {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        if (width > getConfig().map.mobileBreakPoint) {
            this.setState({ padding: getConfig().map.mapPaddings.full }, true, () => {
                this.emit(StoreLocatorEvents.PADDING_CHANGED)
                this.setFullView();
            });
        } else {
            this.setState({ padding: getConfig().map.mapPaddings.mobile }, true, () => {
                this.emit(StoreLocatorEvents.PADDING_CHANGED)
                this.setMobileView();
            });
        }
    }
    manageMarkerFocus(): void {
        const mapContainer = document.getElementById(getConfig().selectors.mapContainerID);
        if (mapContainer) {
            mapContainer.addEventListener('focusin', () => {
                const firstMarker = mapContainer.querySelector('.mapboxgl-marker');
                if (firstMarker) {
                    (firstMarker as HTMLElement).tabIndex = 0;
                }
            });
        }
    }

    getHTMLSkeleton(): string {
        const config = getConfig();
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const isDesktop = width > config.map.mobileBreakPoint;

        const searchContainer = `
        <div id="${config.selectors.searchContainerID}" ${this.shouldHideSearchContainer ? 'style="display:none"' : ''}>
            <div id="${config.selectors.searchWrapperID}"></div>
        </div>
    `;

        const sidebarContent = `
        <div id="${config.selectors.sidebarContentContainerID}">
            <div id="${config.selectors.listStoresContainerID}"></div>
        </div>
    `;

        const sidebar = `
        <div id="${config.selectors.sidebarContainerID}" ${!isDesktop ? 'tabindex="0"' : ''} aria-live="polite">
            ${searchContainer}
            ${sidebarContent}
        </div>
    `;

        const map = `<div id="${config.selectors.mapContainerID}"></div>`;
        const panMapControl = `<div id="${config.selectors.panMapControlID}" class="pan-camera-wrapper"></div>`;

        return isDesktop
            ? `${sidebar}${map}${panMapControl}`
            : `${map}${sidebar}`;
    }

    getPositionOfSelectedStore(store: AssetFeatureResponse): number {
        let offsetTop = 0;
        if (store) {
            const $storeElements: HTMLCollection = this.$sidebarContentContainer.getElementsByClassName('summaryStore');
            if ($storeElements.length > 0) {
                for (const $element of $storeElements as any as HTMLElement[]) {
                    if ($element.dataset.storeId === store.properties.store_id) {
                        $element.classList.add('summaryStore__selected');
                        offsetTop = $element.offsetTop;
                    } else {
                        $element.classList.remove('summaryStore__selected');
                    }
                }
            }
        }
        return offsetTop;
    }

    showSideBarPanel(): void {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const $sidebarContainer = document.getElementById(getConfig().selectors.sidebarContainerID);
        const $searchContainer = document.getElementById(getConfig().selectors.searchContainerID);
        if (this.storeHighlight === true) {
            this.showPanel = true;
        }

        const $panelView = this.showPanel ? 'flex' : 'none';
        if ($sidebarContainer && $searchContainer) {
            if (width > getConfig().map.mobileBreakPoint) {
                $sidebarContainer.style.overflowY = 'auto';
                $sidebarContainer.style.display = 'flex';
                if (document.getElementById(getConfig().selectors.toggleViewContainerID)) {
                    const element = document.getElementById(getConfig().selectors.toggleViewContainerID);
                    if (element) {
                        this.$target.removeChild(element);
                    }
                }
            } else {
                if (this.storeHighlight === true) {
                    $sidebarContainer?.classList.add('sidebarContainerAlt');
                }
                else {
                    const bandHeight = 60;
                    $sidebarContainer.style.maxHeight = "calc(100% - " + (bandHeight) + "px)"
                    $sidebarContainer.style.display = $panelView;
                    if (!document.getElementById(getConfig().selectors.toggleViewContainerID) && $sidebarContainer.style.display === 'flex') {
                        const toggleViewContent = document.createElement('button');
                        toggleViewContent.id = getConfig().selectors.toggleButtonID;
                        toggleViewContent.innerText = getLocale().stores.cta["View Map"];
                        toggleViewContent.addEventListener('click', () => {
                            this.toggleViewForMobile(toggleViewContent);
                        });
                        const $toggleViewContainer = document.createElement('div');
                        $toggleViewContainer.id = getConfig().selectors.toggleViewContainerID;
                        $toggleViewContainer.appendChild(toggleViewContent);
                        this.$target.appendChild($toggleViewContainer);
                    }
                    if ($sidebarContainer.style.display === 'flex') {
                        const toggleButton = document.getElementById(getConfig().selectors.toggleButtonID);
                        if (toggleButton) {
                            toggleButton.innerText = getLocale().stores.cta["View Map"];
                        }
                    } else {
                        const $toggleViewContainer = document.getElementById(getConfig().selectors.toggleViewContainerID);
                        if ($toggleViewContainer) {
                            this.$target.removeChild($toggleViewContainer);
                        }
                    }
                }
            }
        }
    }

    toggleViewForMobile(element: HTMLButtonElement): void {
        const $sidebarContainer = document.getElementById(getConfig().selectors.sidebarContainerID);
        if ($sidebarContainer) {
            if (element.innerHTML === getLocale().stores.cta["View Map"]) {
                $sidebarContainer.style.display = 'none';
                element.innerHTML = getLocale().stores.cta["View List"];
            } else {
                $sidebarContainer.style.display = 'flex';
                element.innerHTML = getLocale().stores.cta["View Map"];
            }
        }
    }

    setMobileView(): void {
        const $mapContainer = document.getElementById(getConfig().selectors.mapContainerID);
        const $searchContainer = document.getElementById(getConfig().selectors.searchContainerID);

        if ($mapContainer && $searchContainer && !$mapContainer.contains($searchContainer)) {
            $mapContainer.insertBefore($searchContainer, $mapContainer.childNodes[0]);

        }
        const $mapBoxControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-top-right');
        if ($mapBoxControl && $searchContainer) {
            $mapBoxControl.style.paddingTop = `${$searchContainer.children[0].clientHeight + 20}px`;
            $mapBoxControl.style.paddingBottom = `30px`;
            $mapBoxControl.classList.remove('mapboxgl-ctrl-top-right');
            $mapBoxControl?.classList.add('mapboxgl-ctrl-bottom-right')
        }
        this.showSideBarPanel();

    }

    setFullView(): void {
        const $mapContainer = document.getElementById(getConfig().selectors.mapContainerID);
        const $searchContainer = document.getElementById(getConfig().selectors.searchContainerID);
        const $sidebarContainer = document.getElementById(getConfig().selectors.sidebarContainerID);
        const $mapBoxControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-top-right');
        if ($mapContainer && $searchContainer && $mapContainer.contains($searchContainer)) {
            $mapContainer.removeChild($searchContainer);
        }
        if ($sidebarContainer && $searchContainer && !$sidebarContainer.contains($searchContainer)) {
            $sidebarContainer.insertBefore($searchContainer, $sidebarContainer.childNodes[0]);
        }
        if ($mapBoxControl) {
            $mapBoxControl.style.paddingBottom = `30px`;
            $mapBoxControl.classList.remove('mapboxgl-ctrl-top-right');
            $mapBoxControl?.classList.add('mapboxgl-ctrl-bottom-right');
        }
        this.showSideBarPanel();
    }

    scrollToSelectedStore(store: AssetFeatureResponse): void {
        const $searchContainer = document.getElementById(getConfig().selectors.searchContainerID);
        let scrollTo = 0;
        if (store) {
            scrollTo = this.getPositionOfSelectedStore(store);
            const margin = $searchContainer ? ($searchContainer.clientHeight + 2) : 0;
            scrollTo = scrollTo - margin;
            this.$sidebarContentContainer.scrollTo(0, scrollTo);
            const storeCardEl = this.$sidebarContentContainer.querySelector(`[data-store-id='${store.properties.store_id}']`) as HTMLElement;
            if (storeCardEl) {
                storeCardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }

    async getUserPosition(): Promise<void> {
        try {
            const params = Object.assign({});
            this.api = new WoosmapApiClient({ apiKey: getConfig().map.woosmapPublicKey });
            const geoLocationResponse = this.woosGeoLocation ? this.woosGeoLocation : await this.api.geoLocation.getCurrentPosition(params);
            const searchParams = {
                lat: geoLocationResponse.latitude,
                lng: geoLocationResponse.longitude,
                limit: 1,
                query: getConfig().map.baseFilter
            };
            const stores: AssetFeatureCollectionResponse = await this.api.stores.searchStores(searchParams);
            const firstStore: AssetFeatureResponse = stores.features[0];
            this.nearestStoreToUser = firstStore;
            const distanceToFirstStore: number = stores.features[0].properties.distance || geoLocationResponse.accuracy * 1000;
            const $distanceToNearestStore = document.getElementById('distanceToNearestStore');
            if ($distanceToNearestStore) {
                $distanceToNearestStore.innerHTML = `Nearest store (<i>${firstStore.properties.name}</i>) from your location is ${(distanceToFirstStore / 1000).toFixed(2)} km far away`;
            }

            if ((distanceToFirstStore / 1000) < geoLocationResponse.accuracy) {
                this.centerMapAtUserPosition(geoLocationResponse);
            } else {
                this.centerMapAtUserPosition(geoLocationResponse, firstStore);

            }
            /** If the behaviour is expected ditto like browser location uncomment this part */
            // const locality = {lat: geoLocationResponse.latitude, lng: geoLocationResponse.longitude}
            //     this.storesListComponent.setState({
            //         nearbyLocation: locality,
            //         query: getConfig().map.baseFilter
            //     }, true, () =>
            //         this.storesListComponent.emit(StoresListComponentEvents.LOCALITY_CHANGED)
            // );

        } catch (error) {
            console.error('An error occurred while getting user position:', error);
        }
    }
    centerMapAtUserPosition(userPositon: GeoLocationResponse, store?: AssetFeatureResponse): void {
        const { latitude, longitude, accuracy } = userPositon;
        let zoom = 5;
        let bounds: woosmap.map.LatLngBounds;
        const center: woosmap.map.LatLng = new woosmap.map.LatLng(latitude, longitude);
        if (userPositon) {
            if (store) {
                bounds = new woosmap.map.LatLngBounds();
                /**Reverted to old logic after confirmation from client */
                bounds.extend({
                    lat: store.geometry.coordinates[1],
                    lng: store.geometry.coordinates[0]
                });
                bounds.extend({
                    lat: userPositon.latitude,
                    lng: userPositon.longitude
                });
            } else {
                if (accuracy <= getConfig().map.geoLocationOptions.radius) {
                    zoom = getConfig().map.geoLocationOptions.zoom;
                    const $zoomLevel = document.getElementById("zoomLevel");
                    if ($zoomLevel) {
                        $zoomLevel.innerHTML = `Zoom Level: ${zoom}`;
                    }
                }
            }
            this.mapComponent.setState({
                userCurrentPosition: { lat: center.lat(), lng: center.lng() },
                zoom: zoom
            }, true, () => {
                this.mapComponent.centerMapToUserPosition();
                if (bounds && store) {
                    this.mapComponent.map.fitBounds(bounds, this.state.padding, true);
                    // this.mapComponent.map.flyTo({
                    //     center: {lat: store.geometry.coordinates[1], lng: store.geometry.coordinates[0]},
                    //     zoom: 10,
                    //     padding: this.state.padding,
                    //     animate: true
                    // });
                }
            }
            );
        }
    }
    preloadLinks(): void {
        Promise.all(
            [
                preloadLink({
                    url: getConfig().urls.mapJS,
                    params: { key: getConfig().map.woosmapPublicKey, language: getLocaleLang().toLowerCase() },
                    as: "script",
                }),
                preloadLink({
                    url: getConfig().urls.localitiesWidgetJS,
                    params: { key: getConfig().map.woosmapPublicKey },
                    as: "script",
                }),
                preloadLink({
                    url: getConfig().urls.localitiesWidgetCSS,
                    as: "style",
                }),
                preloadLink({
                    url: "https://api.woosmap.com/maps/tiles.json",
                    params: { key: getConfig().map.woosmapPublicKey },
                    as: "fetch",
                    crossOrigin: "anonymous"
                }),
                preloadLink({
                    url: "https://api.woosmap.com/maps/style/streets.json",
                    params: { language: getLocaleLang().toLowerCase(), key: getConfig().map.woosmapPublicKey },
                    as: "fetch",
                    crossOrigin: "anonymous"
                }),
                preloadLink({
                    url: "https://sdk.woosmap.com/map/assets/sprite@2x.png",
                    as: "fetch",
                    crossOrigin: "anonymous"
                }),
                preloadLink({
                    url: "https://sdk.woosmap.com/map/assets/sprite@2x.json",
                    as: "fetch",
                    crossOrigin: "anonymous"
                }),
            ]
        )
    }

    showBlueDot(position: woosmap.map.LatLngLiteral): void {
        if (this.blueDotMarker) {
            this.blueDotMarker.setPosition(position);
            this.blueDotMarker.set("title", getLocale().search.yourLocation);
        }
        else {
            this.blueDotMarker = new woosmap.map.Marker({
                position: position,
                icon: {
                    url: bludeDot,
                    scaledSize: {
                        height: 32,
                        width: 32,
                    },
                },
                title: getLocale().search.yourLocation,
            });
            this.blueDotMarker.setMap(this.mapComponent.map);
        }
    }
    async locateStore(store: string): Promise<void> {
        this.mapComponent.disableUserInteraction();
        this.storesListComponent.setState({ selectedStore: undefined });
        const searchParams = {
            limit: 1,
            query: getConfig().map.baseFilter + `user.studioID:="${store}"`
        };
        this.api = new WoosmapApiClient({ apiKey: getConfig().map.woosmapPublicKey });
        const stores: AssetFeatureCollectionResponse = await this.api.stores.searchStores(searchParams);
        const storeFound: AssetFeatureResponse = stores.features[0];
        if (storeFound) {
            const zoomToSet = this.isForSuccessPage ? 14 : 12;
            this.mapComponent.setState({ selectedStore: storeFound, stores: [storeFound], zoom: zoomToSet }, true, () => {
                this.mapComponent.emit(MapComponentEvents.STORES_CHANGED);
            }
            );
            this.storesListComponent.setState({ selectedStore: storeFound, highlightStore: true, expandedView: false, stores: [storeFound], showOnSuccess: this.isForSuccessPage });
            this.storesListComponent.emit(StoresListComponentEvents.STORE_SELECTED, storeFound);
            this.storesListComponent.emit(StoresListComponentEvents.STORE_MOUSEENTER, storeFound);
        }
        else {
            this.getUserPosition();
        }
        this.mapComponent.enableUserInteraction();
    }
    adjustPanelHeight(): void {
        const $ctaElements = document.getElementsByClassName("summaryStore__ctacontainer");
        const $sidebarContainer = document.getElementById(getConfig().selectors.sidebarContainerID);
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        if ($sidebarContainer && width < getConfig().map.mobileBreakPoint) {
            if ($ctaElements && $ctaElements.length > 0 && $ctaElements[0].hasChildNodes()) {
                $sidebarContainer.style.maxHeight = getConfig().map.baseFilter === 'type: "JR"' ? "186px" : "161px";
            }
            else {
                $sidebarContainer.style.maxHeight = getConfig().map.baseFilter === 'type: "JR"' ? "146px" : "119px";
            }
            const $mapBoxControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-bottom-right');
            const $mapBoxInfoControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-attrib.mapboxgl-compact');
            if ($mapBoxControl && $mapBoxInfoControl) {
                $mapBoxInfoControl.style.bottom = $sidebarContainer.clientHeight + "px";
                $mapBoxControl.style.bottom = $sidebarContainer.clientHeight + 5 + "px";
            }

        }
        else {
            if ($sidebarContainer) {
                $sidebarContainer.style.maxHeight = "unset";
            }
            const $mapBoxInfoControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-attrib.mapboxgl-compact');
            const $mapBoxControl = document.querySelector<HTMLDivElement>('.mapboxgl-ctrl-bottom-right');
            if ($mapBoxInfoControl && $mapBoxControl) {
                $mapBoxInfoControl.style.bottom = "";
                $mapBoxControl.style.bottom = "0px";
            }
        }
    }
    updateMapControlPosition(): void {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const mobileBreakPoint = getConfig().map.mobileBreakPoint;

        const shouldSkipClassRemoval = (element: Element, checkClass: string): boolean => {
            const firstChild = element.firstElementChild as HTMLElement | null;
            return !!firstChild && firstChild.classList.contains(checkClass);
        };
        const toggleControlClass = (addClass: string, removeClass: string) => {
            document.querySelectorAll(`.${removeClass}`).forEach((element) => {
                if (!shouldSkipClassRemoval(element, 'mapboxgl-ctrl-attrib')) {
                    element.classList.remove(removeClass);
                    element.classList.add(addClass);
                    if (addClass === 'mapboxgl-ctrl-top-right') {
                        (element as HTMLDivElement).style.paddingTop = "0px";
                    }
                }
            });
        };
        const isMobile = width <= mobileBreakPoint;
        toggleControlClass(isMobile ? 'mapboxgl-ctrl-bottom-right' : 'mapboxgl-ctrl-top-right', isMobile ? 'mapboxgl-ctrl-top-right' : 'mapboxgl-ctrl-bottom-right');
    }
    async getAllStores(): Promise<woosmap.map.stores.StoreResponse[]> {
        const allStores: woosmap.map.stores.StoreResponse[] = [];
        const query: woosmap.map.stores.StoresSearchRequest = { storesByPage: 300, query: getConfig().map.baseFilter, page: 1 };
        const storesService = new woosmap.map.StoresService();

        function getStores(storePage?: number): Promise<woosmap.map.stores.StoreResponse[]> {
            if (storePage) {
                query.page = storePage;
            }
            return storesService
                .search(query)
                .then((response) => {
                    allStores.push(...response.features);
                    if (query.page === response.pagination.pageCount) {
                        return allStores;
                    }
                    return getStores(response.pagination.page + 1);
                })
                .catch((err) => {
                    console.error(err);
                    throw new Error("Error getting all stores");
                });
        }

        return getStores();
    }
    filterStoresByBounds(
        stores: woosmap.map.stores.StoreResponse[],
        bounds: woosmap.map.LatLngBounds | null,
    ): woosmap.map.stores.StoreResponse[] {
        return stores.filter((store: woosmap.map.stores.StoreResponse) =>
            bounds
                ? bounds.contains({
                    lat: store.geometry.coordinates[1],
                    lng: store.geometry.coordinates[0],
                })
                : false,
        );
    }
    buildMapPanControl(): void {
        const $panMapWrapperContainer = document.getElementById(getConfig().selectors.panMapControlID);
        const $btnExpand = document.createElement('button');
        $btnExpand.className = "expand-btn";
        $btnExpand.setAttribute("aria-label", "Map camera controls");
        $btnExpand.setAttribute("aria-expanded", "false");
        $btnExpand.setAttribute("aria-controls", "pan-camera-control");

        const $panMapCameraControl = document.createElement('div');
        $panMapCameraControl.className = 'pan-camera-control';
        $panMapCameraControl.setAttribute("aria-label", "Map camera controls");
        $panMapCameraControl.setAttribute('role', "group");
        $panMapCameraControl.style.display = "none";

        const $panMapCenter = document.createElement('div');
        $panMapCenter.className = 'pan-center';


        const directions = ['up', 'down', 'left', 'right'];

        directions.forEach(dir => {
            const btn = document.createElement('button');
            btn.className = `pan-btn pan-${dir}`;
            btn.setAttribute('aria-label', `Pan ${dir}`);
            btn.innerHTML = getArrowSymbol(dir);
            btn.addEventListener('click', () => this.moveMap(dir));
            $panMapCameraControl.appendChild(btn);
        });
        $panMapCameraControl.appendChild($panMapCenter);
        if ($panMapWrapperContainer) {
            $panMapWrapperContainer.appendChild($btnExpand);
            $panMapWrapperContainer.appendChild($panMapCameraControl);
        }
        $btnExpand.addEventListener('click', () => {
            const isExpanded = $btnExpand.getAttribute("aria-expanded") === "true";
            $btnExpand.setAttribute("aria-expanded", String(!isExpanded));
            $panMapCameraControl.style.display = isExpanded ? "none" : "grid";
        });

    }
    moveMap(direction: string): void {
        this.mapComponent.emit(MapComponentEvents.MAP_PANNED, direction);
    }
}
